import React, { useState } from 'react';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DeleteOutline, EventNoteOutlined } from '@mui/icons-material';
import { addPaymentMilestone, deletePaymentMilestone, getPaymentMilestones, planningError, setMilestonePaid } from '../actions/planning';
import { dateBounds } from '../utils/planningDates';

export default function PaymentMilestones({ budget }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ title: '', amount: '', due_date: '' });
  const run = async action => {
    setBusy(true); setError('');
    try { await action(); setItems(await getPaymentMilestones(budget.id)); }
    catch (reason) { setError(planningError(reason)); }
    finally { setBusy(false); }
  };
  return <>
    <Tooltip title="Payment milestones" arrow>
      <IconButton size="small" color="primary" aria-label="Payment milestones" onClick={() => { setOpen(true); run(async () => {}); }}>
        <EventNoteOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
    <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Payment milestones / {budget.name}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>A planning checklist, not a payment processor. Marking a milestone paid does not change the expense's recorded payments; keep those up to date in Budget Tracking.</Alert>
        <Typography variant="body2" sx={{ mb: 2 }}>Scheduled: {items.reduce((sum, item) => sum + Number(item.amount), 0).toFixed(2)} of {Number(budget.expense).toFixed(2)}. Maximum 20 milestones per expense.</Typography>
        {items.map(item => <Stack direction="row" alignItems="center" spacing={1} key={item.id}>
          <Checkbox checked={Boolean(item.paid_at)} disabled={busy} inputProps={{ 'aria-label': `Mark ${item.title} paid` }} onChange={e => run(() => setMilestonePaid(item.id, e.target.checked))} />
          <Stack sx={{ flex: 1, minWidth: 0 }}><Typography sx={{ overflowWrap: 'anywhere' }}>{item.title}</Typography><Typography variant="caption" color="text.secondary">{Number(item.amount).toFixed(2)} / due {item.due_date}</Typography></Stack>
          <IconButton aria-label={`Delete ${item.title}`} disabled={busy} onClick={() => run(() => deletePaymentMilestone(item.id))}><DeleteOutline /></IconButton>
        </Stack>)}
        {!items.length && <Typography color="text.secondary">Plan a deposit, balance, or other payment deadline.</Typography>}
        <Divider sx={{ my: 2 }} />
        <Stack component="form" id={`milestone-form-${budget.id}`} spacing={2} onSubmit={e => {
          e.preventDefault();
          run(async () => { await addPaymentMilestone(budget.id, form); setForm({ title: '', amount: '', due_date: '' }); });
        }}>
          <TextField required label="Milestone name" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} inputProps={{ maxLength: 100 }} />
          <TextField required label="Amount" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} inputProps={{ min: '0.01', step: '0.01', max: budget.expense }} />
          <TextField required label="Due date" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} inputProps={dateBounds()} slotProps={{ inputLabel: { shrink: true } }} />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}><Button disabled={busy} onClick={() => setOpen(false)}>Close</Button><Button form={`milestone-form-${budget.id}`} type="submit" variant="contained" disabled={busy || Number(budget.expense) <= 0 || items.length >= 20}>Add milestone</Button></DialogActions>
    </Dialog>
  </>;
}
